import React, { useState } from "react";

const plans = [
  {
    id: 1,
    name: "Starter",
    description:
      "🚀 All-in-one powerful plan with complete access to every Zotrai feature.",
    monthly: 2999,
    annually: 29999,
    popular: true,
    emoji: "🚀",
    features: [
      "Search, compare, and book flights from top global suppliers in one place",
      "Access and connect with 1,000+ verified global DMCs",
      "Smart accounting suite with ledgers, invoices, supplier settlements, and corporate payments",
      "Capture leads directly from social media and run promotions from a single dashboard",
      "Generate professional itineraries in minutes using AI",
      "AI-powered visa processing and document management system",
      "One unified platform for Flights, Hotels, Visas, Cars, Sightseeing, and more",
      "Integrated and secure payment gateway",
      "Plug in and manage your own supplier APIs seamlessly",
      "AI-based WhatsApp automation for leads, follow-ups, and customer support",
      "Unlimited email marketing and promotional campaigns",
      "Advanced reports and analytics for sales, revenue, and performance tracking",
    ],
    addons: [
      "Launch your own B2C booking website for customers in minutes with yearly subscription",
      "Launch a full-featured B2C travel portal with flights, hotels, and packages using your preferred suppliers – INR 1,999",
      "Launch a B2B travel portal for your agent network with flights, hotels, and packages – INR 1,999",
      "Launch a B2E corporate travel portal for your business clients with flights, hotels, and packages – INR 1,999",
    ],
  },
];

const getAccentColor = (planName) => {
  if (planName === "Starter") return "#10B981";
  if (planName === "Pro") return "#3B82F6";
  return "#8B5CF6";
};

export default function PricingSection({
  onPlanClick,
  buttonText = "Start Free Trial",
}) {
  const [billingCycle, setBillingCycle] = useState(1); // 1 = Monthly, 2 = Annually

  return (
    <section
      className="py-5"
      style={{
        background: "linear-gradient(180deg, #F8F9FC 0%, #E9EFF6 100%)",
      }}
    >
      <div className="container">
        <div className="text-center mb-5">
          <h2 className="display-5 fw-bold text-dark mb-2">Plans & Pricing</h2>
          <p className="text-muted fs-5">
            Pick the plan that's right for your business growth
          </p>
          <div className="d-inline-flex bg-white p-1 rounded-pill shadow-sm border mt-3">
            <button
              className={`btn rounded-pill px-4 py-2 fw-semibold ${billingCycle === 1 ? "btn-primary shadow-sm" : "btn-link text-muted"}`}
              onClick={() => setBillingCycle(1)}
              style={{ textDecoration: "none" }}
            >
              Monthly
            </button>
            <button
              className={`btn rounded-pill px-4 py-2 fw-semibold ${billingCycle === 2 ? "btn-primary shadow-sm" : "btn-link text-muted"}`}
              onClick={() => setBillingCycle(2)}
              style={{ textDecoration: "none" }}
            >
              Annually{" "}
              <span
                className="ms-1 badge bg-warning text-dark"
                style={{ fontSize: "10px" }}
              >
                Save 20%
              </span>
            </button>
          </div>
        </div>

        <div className="row justify-content-center">
          {plans.map((plan) => {
            const isMonthly = billingCycle === 1;
            const accentColor = getAccentColor(plan.name);
            return (
              <div key={plan.id} className="col-lg-10 col-12 mb-5">
                <div
                  className="card shadow-lg overflow-hidden border-0"
                  style={{
                    borderRadius: "20px",
                    borderTop: `6px solid ${accentColor}`,
                    transition: "transform 0.3s ease",
                  }}
                >
                  {plan.popular && (
                    <div
                      className="position-absolute top-0 end-0 bg-primary text-white px-4 py-2 fw-bold"
                      style={{ borderBottomLeftRadius: "20px", zIndex: 10 }}
                    >
                      ⭐ MOST POPULAR
                    </div>
                  )}

                  <div className="row g-0 align-items-stretch">
                    {/* Left Side: Pricing */}
                    <div
                      className="col-lg-5 p-5 text-center border-end d-flex flex-column justify-content-center"
                      style={{
                        backgroundColor: plan.popular ? "#F4F9FF" : "#ffffff",
                      }}
                    >
                      <div className="mb-2 fs-1">{plan.emoji}</div>
                      <h3 className="fw-bold text-dark mb-2">{plan.name}</h3>
                      <p className="text-muted small px-3 mb-4">
                        {plan.description}
                      </p>

                      <div className="mb-4">
                        <span className="display-5 fw-bold text me-1">INR</span>
                        <span className="display-5 fw-bold text-dark">
                          {isMonthly ? plan.monthly : plan.annually}
                        </span>
                        <div className="text-muted small fw-medium mt-n1">
                          per {isMonthly ? "month" : "year"}
                        </div>
                      </div>

                      <button
                        onClick={onPlanClick}
                        className="btn btn-lg w-100 rounded-pill fw-bold py-3 shadow"
                        style={{
                          backgroundColor: accentColor,
                          color: "#fff",
                          border: "none",
                        }}
                      >
                        {buttonText}{" "}
                        <i className="ri-arrow-right-line ms-2"></i>
                      </button>
                    </div>

                    {/* Right Side: Features */}
                    <div
                      className="col-lg-7 p-4 p-lg-5"
                      style={{ backgroundColor: "#FBFDFF" }}
                    >
                      <h5
                        className="text-uppercase fw-bold mb-1 ls-1"
                        style={{ color: accentColor }}
                      >
                        Included in {plan.name}
                      </h5>
                      <div className="row g-4 text-start">
                        {plan.features.map((feature, i) => (
                          <div key={i} className="col-md-12">
                            <div className="d-flex align-items-start gap-2">
                              <div
                                className="rounded-circle p-1 d-flex flex-shrink-0"
                                style={{ backgroundColor: `${accentColor}15` }}
                              >
                                <i
                                  className="ri-check-line fw-bold"
                                  style={{
                                    fontSize: "14px",
                                    color: accentColor,
                                  }}
                                ></i>
                              </div>
                              <span className="text-dark fw-medium">
                                {feature}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                      <h5
                        className="text-uppercase fw-bold mb-4 ls-1 mt-4"
                        style={{ color: accentColor }}
                      >
                        Add-Ons
                      </h5>
                      {plan.addons.map((feature, i) => (
                        <div key={i} className="col-md-12 p-2">
                          <div className="d-flex align-items-start gap-2">
                            <div
                              className="rounded-circle p-1 d-flex flex-shrink-0"
                              style={{ backgroundColor: `${accentColor}15` }}
                            >
                              <i
                                className="ri-check-line fw-bold"
                                style={{ fontSize: "14px", color: accentColor }}
                              ></i>
                            </div>
                            <span className="text-dark fw-medium">
                              {feature}
                            </span>
                          </div>
                        </div>
                      ))}
                      <div className="mt-5 pt-4 border-top d-flex align-items-center justify-content-between text-muted small">
                        <span>Transparent pricing, no hidden fees.</span>
                        <span
                          className="fw-semibold"
                          style={{ color: accentColor, cursor: "pointer" }}
                        >
                          Need help? Chat with us
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
